import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, map, tap, throwError } from 'rxjs';
import {
  canAccessBatteryCatalogAdmin,
  canAccessResellerWorkspace,
  canAdvanceOrderStatus,
  canApprovePoSales,
  canApprovePoSupport,
  canAssignAdminOrgAuthority,
  canManageUsers,
  canRejectPo,
  canViewAdminOrgEvents,
  canViewEvents,
  canViewResellerEvents,
  hasAdminProjectsAccess,
  hasCustomerProfilesAdminAccess,
  hasFullAdminOrgAccess,
  isInAdminOrg,
  isResellerAdmin,
  isSuperAdmin,
} from './auth-capabilities';
import { LoginCredentials, LoginResponse } from './types/login.interface';
import { IAuthUser, IJWToken } from './types/auth.interface';
import { AuthUser } from '../models/user.model';
import { ApiService } from '../transport/api.service';
import { Endpoints } from '../transport/endpoints';

type AuthStatus = 'pending' | 'authenticated' | 'unauthenticated';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly defaultAdminResellerKey = 'EMSYS';

  authStatus: AuthStatus;
  user$: BehaviorSubject<IAuthUser | null>;
  private _isAuthenticated: BehaviorSubject<boolean>;

  token: string | null;

  constructor() {
    this.token = localStorage.getItem('token');
    this.authStatus = 'pending';
    this.user$ = new BehaviorSubject<IAuthUser | null>(null);
    this._isAuthenticated = new BehaviorSubject<boolean>(false);
  }

  me(): Observable<IAuthUser> {
    return this.api.get<IAuthUser>(
      Endpoints.auth.me,
      undefined,
      { userVisibleErrors: false },
    ).pipe(
      tap((response: IAuthUser) => {
        this.setAuthenticatedUser(response);
      }),
      catchError((error) => {
        this.resetSession();
        return throwError(() => error);
      }),
    );
  }

  refreshCurrentUser(): Observable<IAuthUser> {
    return this.me();
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.api.post<LoginResponse>(Endpoints.auth.login, credentials).pipe(
      tap((response: LoginResponse) => {
        this.token = response.token;
        localStorage.setItem('token', response.token);
        this.setAuthenticatedUser(response.user);
      }),
      catchError((error) => {
        this.setUnauthenticatedState();
        return throwError(() => error);
      }),
    );
  }

  logout(): Observable<void> {
    return this.api.delete<void>(Endpoints.auth.logout).pipe(
      tap(() => {
        this.resetSession();
        void this.router.navigate(['/login']);
      }),
    );
  }

  sendResetPasswordLink(email: string): Observable<unknown> {
    return this.api.post(Endpoints.auth.resetPassword, { email });
  }

  resetPassword(payload: {
    token: string;
    password: string;
    confirm_password: string;
  }): Observable<unknown> {
    return this.api.put(Endpoints.auth.resetPasswordConfirm, payload);
  }

  changePassword(payload: {
    current_password: string;
    new_password: string;
  }): Observable<unknown> {
    return this.api.put(Endpoints.auth.me, payload);
  }

  get isAuthenticated$(): Observable<boolean> {
    return this._isAuthenticated.asObservable();
  }

  get hasAdminProjectsAccess$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => hasAdminProjectsAccess(user, this.adminResellerKey)),
    );
  }

  get hasAdminProjectsAccessSync(): boolean {
    return hasAdminProjectsAccess(this.user$.getValue(), this.adminResellerKey);
  }

  get hasFullAdminOrgAccess$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => hasFullAdminOrgAccess(user, this.adminResellerKey)),
    );
  }

  get hasFullAdminOrgAccessSync(): boolean {
    return hasFullAdminOrgAccess(this.user$.getValue(), this.adminResellerKey);
  }

  get canAccessBatteryCatalogAdmin$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canAccessBatteryCatalogAdmin(user, this.adminResellerKey)),
    );
  }

  get canAccessBatteryCatalogAdminSync(): boolean {
    return canAccessBatteryCatalogAdmin(this.user$.getValue(), this.adminResellerKey);
  }

  get isInAdminOrg$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => isInAdminOrg(user, this.adminResellerKey)),
    );
  }

  get isInAdminOrgSync(): boolean {
    return isInAdminOrg(this.user$.getValue(), this.adminResellerKey);
  }

  get canAdvanceOrderStatus$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canAdvanceOrderStatus(user, this.adminResellerKey)),
    );
  }

  get canAdvanceOrderStatusSync(): boolean {
    return canAdvanceOrderStatus(this.user$.getValue(), this.adminResellerKey);
  }

  get canApprovePoSales$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canApprovePoSales(user, this.adminResellerKey)),
    );
  }

  get canApprovePoSalesSync(): boolean {
    return canApprovePoSales(this.user$.getValue(), this.adminResellerKey);
  }

  get canApprovePoSupport$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canApprovePoSupport(user, this.adminResellerKey)),
    );
  }

  get canApprovePoSupportSync(): boolean {
    return canApprovePoSupport(this.user$.getValue(), this.adminResellerKey);
  }

  get canRejectPo$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canRejectPo(user, this.adminResellerKey)),
    );
  }

  get canRejectPoSync(): boolean {
    return canRejectPo(this.user$.getValue(), this.adminResellerKey);
  }

  get hasCustomerProfilesAdminAccess$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => hasCustomerProfilesAdminAccess(user, this.adminResellerKey)),
    );
  }

  get hasCustomerProfilesAdminAccessSync(): boolean {
    return hasCustomerProfilesAdminAccess(this.user$.getValue(), this.adminResellerKey);
  }

  get hasSuperAdminAccess(): Observable<boolean> {
    return this.isSuperAdmin$;
  }

  get isSuperAdmin$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => isSuperAdmin(user)),
    );
  }

  get isSuperAdminSync(): boolean {
    return isSuperAdmin(this.user$.getValue());
  }

  get hasAdminProjectsAccessRuntimeSync(): boolean {
    if (this.user$.getValue() != null) {
      return this.hasAdminProjectsAccessSync;
    }

    return this.tokenHasAdminProjectsAccess;
  }

  get hasFullAdminOrgAccessRuntimeSync(): boolean {
    if (this.user$.getValue() != null) {
      return this.hasFullAdminOrgAccessSync;
    }

    return this.tokenHasFullAdminOrgAccess;
  }

  get canAccessBatteryCatalogAdminRuntimeSync(): boolean {
    if (this.user$.getValue() != null) {
      return this.canAccessBatteryCatalogAdminSync;
    }

    return canAccessBatteryCatalogAdmin(this.tokenInfo, this.adminResellerKey);
  }

  get canAdvanceOrderStatusRuntimeSync(): boolean {
    if (this.user$.getValue() != null) {
      return this.canAdvanceOrderStatusSync;
    }

    return this.tokenCanAdvanceOrderStatus;
  }

  get hasCustomerProfilesAdminAccessRuntimeSync(): boolean {
    if (this.user$.getValue() != null) {
      return this.hasCustomerProfilesAdminAccessSync;
    }

    return this.tokenHasCustomerProfilesAdminAccess;
  }

  get canViewAdminOrgEventsRuntimeSync(): boolean {
    if (this.user$.getValue() != null) {
      return this.canViewAdminOrgEventsSync;
    }

    return this.tokenCanViewAdminOrgEvents;
  }

  get canManageUsers$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canManageUsers(user)),
    );
  }

  get canManageUsersSync(): boolean {
    return canManageUsers(this.user$.getValue());
  }

  get isResellerAdmin$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => isResellerAdmin(user, this.adminResellerKey)),
    );
  }

  get isResellerAdminSync(): boolean {
    return isResellerAdmin(this.user$.getValue(), this.adminResellerKey);
  }

  get isResellerAdminRuntimeSync(): boolean {
    if (this.user$.getValue() != null) {
      return this.isResellerAdminSync;
    }

    return this.tokenIsResellerAdmin;
  }

  get canAccessResellerWorkspace$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canAccessResellerWorkspace(user)),
    );
  }

  get canAccessResellerWorkspaceSync(): boolean {
    return canAccessResellerWorkspace(this.user$.getValue());
  }

  get canViewEvents$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canViewEvents(user, this.adminResellerKey)),
    );
  }

  get canViewEventsSync(): boolean {
    return canViewEvents(this.user$.getValue(), this.adminResellerKey);
  }

  get canViewResellerEvents$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canViewResellerEvents(user, this.adminResellerKey)),
    );
  }

  get canViewResellerEventsSync(): boolean {
    return canViewResellerEvents(this.user$.getValue(), this.adminResellerKey);
  }

  get canViewAdminOrgEvents$(): Observable<boolean> {
    return this.user$.pipe(
      map((user) => canViewAdminOrgEvents(user, this.adminResellerKey)),
    );
  }

  get canViewAdminOrgEventsSync(): boolean {
    return canViewAdminOrgEvents(this.user$.getValue(), this.adminResellerKey);
  }

  get canViewPricing$(): Observable<boolean> {
    return this.hasFullAdminOrgAccess$;
  }

  get canViewPricingSync(): boolean {
    return this.hasFullAdminOrgAccessSync;
  }

  get canEditPricing$(): Observable<boolean> {
    return this.isSuperAdmin$;
  }

  get canEditPricingSync(): boolean {
    return this.isSuperAdminSync;
  }

  get canApproveSpecialPricing$(): Observable<boolean> {
    return this.hasFullAdminOrgAccess$;
  }

  get canApproveSpecialPricingSync(): boolean {
    return this.hasFullAdminOrgAccessSync;
  }

  get canGenerateQuoteOverrides$(): Observable<boolean> {
    return this.hasFullAdminOrgAccess$;
  }

  get canGenerateQuoteOverridesSync(): boolean {
    return this.hasFullAdminOrgAccessSync;
  }

  get canDeleteBatteryModels$(): Observable<boolean> {
    return this.hasFullAdminOrgAccess$;
  }

  get canDeleteBatteryModelsSync(): boolean {
    return this.hasFullAdminOrgAccessSync;
  }

  get tokenHasAdminProjectsAccess(): boolean {
    return hasAdminProjectsAccess(this.tokenInfo, this.adminResellerKey);
  }

  get tokenHasFullAdminOrgAccess(): boolean {
    return hasFullAdminOrgAccess(this.tokenInfo, this.adminResellerKey);
  }

  get tokenCanAdvanceOrderStatus(): boolean {
    return canAdvanceOrderStatus(this.tokenInfo, this.adminResellerKey);
  }

  get tokenHasCustomerProfilesAdminAccess(): boolean {
    return hasCustomerProfilesAdminAccess(this.tokenInfo, this.adminResellerKey);
  }

  get tokenHasSuperAdminAccess(): boolean {
    return isSuperAdmin(this.tokenInfo);
  }

  get tokenCanViewAdminOrgEvents(): boolean {
    return canViewAdminOrgEvents(this.tokenInfo, this.adminResellerKey);
  }

  get tokenIsResellerAdmin(): boolean {
    return isResellerAdmin(this.tokenInfo, this.adminResellerKey);
  }

  canAssignAdminOrgAuthority(targetReseller: string | null): boolean {
    return canAssignAdminOrgAuthority(this.tokenInfo, targetReseller, this.adminResellerKey);
  }

  private get adminResellerKey(): string {
    return this.tokenInfo?.admin_reseller_key ?? this.defaultAdminResellerKey;
  }

  resetSession(): void {
    localStorage.removeItem('token');
    this.token = null;
    this.setUnauthenticatedState();
  }

  private setAuthenticatedUser(user: IAuthUser): void {
    this.authStatus = 'authenticated';
    this.user$.next(new AuthUser(user));
    this._isAuthenticated.next(true);
  }

  private setUnauthenticatedState(): void {
    this.authStatus = 'unauthenticated';
    this.user$.next(null);
    this._isAuthenticated.next(false);
  }

  tokenInvalidOrExpired(token: string | null): boolean {
    const expiration = this.parseToken(token)?.exp;

    if (! expiration) {
      return true;
    }

    return this.getDateNow() >= expiration;
  }

  private parseToken(token: string | null): IJWToken | null {
    try {
      if (! token) {
        return null;
      }

      return JSON.parse(atob(token.split('.')[1]));
    } catch (error: unknown) {
      console.error(error);
      return null;
    }
  }

  private getDateNow(): number {
    return Math.floor((new Date).getTime() / 1000);
  }

  public get tokenInfo(): IJWToken | null {
    return this.parseToken(this.token);
  }
}
